import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MapPin, Navigation } from "lucide-react";
import { API, axios } from "@/api";
import { BrandLogo } from "@/components/shared/BrandLogo";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";

export default function NearMeNow({ city }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usingGeo, setUsingGeo] = useState(false);
  const [locationLabel, setLocationLabel] = useState(null);
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { geo } = useAuth();

  useEffect(() => {
    // Try browser geolocation first
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUsingGeo(true);
          setLocationLabel("Using location");
          axios.get(`${API}/offers/near-me?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}&limit=10`)
            .then(r => setItems(r.data))
            .catch(() => fetchFallback())
            .finally(() => setLoading(false));
        },
        () => {
          // GPS denied — try IP fallback coords
          if (geo?.ipLocation?.latitude && geo?.ipLocation?.longitude) {
            setLocationLabel(`Near ${geo.ipLocation.city || "you"}`);
            axios.get(`${API}/offers/near-me?lat=${geo.ipLocation.latitude}&lng=${geo.ipLocation.longitude}&limit=10`)
              .then(r => setItems(r.data))
              .catch(() => fetchFallback())
              .finally(() => setLoading(false));
          } else {
            fetchFallback();
          }
        },
        { timeout: 5000 }
      );
    } else {
      fetchFallback();
    }
  }, [geo?.ipLocation]);

  const fetchFallback = () => {
    setLocationLabel(city || "Saved city");
    axios.get(`${API}/offers/near-me?limit=10`)
      .then(r => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  if (loading || items.length === 0) return null;

  return (
    <section className="mb-5" data-testid="near-me-section">
      <div className="flex items-center justify-between px-4 mb-3">
        <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
          <Navigation className="w-4 h-4" style={{ color: theme.primary }} />
          Near Me Now
        </h2>
        <span className="text-[10px] text-slate-400 flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          {usingGeo ? "Using location" : locationLabel || city || "Saved city"}
        </span>
      </div>
      <div className="overflow-x-auto pb-2 -mx-4 px-4">
        <div className="flex gap-3">
          {items.slice(0, 8).map((item, i) => (
            <motion.div
              key={item.business_id || i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: i * 0.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => navigate(`/offer/${item.business_id}`)}
              className="min-w-[160px] max-w-[160px] bg-white rounded-2xl overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.06)] border border-slate-100/80 flex-shrink-0 cursor-pointer"
              data-testid={`near-me-card-${item.business_id}`}
            >
              <div className="p-3.5 pb-2 flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0">
                  <BrandLogo name={item.name} logoUrl={item.logo_url} size="sm" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-[12px] text-slate-800 truncate">{item.name}</h3>
                  <span className="text-[10px] text-slate-400">{item.category}</span>
                </div>
              </div>
              <div className="px-3.5 pb-3">
                <p className="text-[11px] text-slate-500 line-clamp-1">{item.offer_title || "Birthday deal"}</p>
                {item.offer_value_est > 0 && (
                  <span className="text-[11px] font-bold text-emerald-600">~${item.offer_value_est}</span>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
